import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, Input } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngrx/store";
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation, fadeInExpandOnEnterAnimation, fadeOutCollapseOnLeaveAnimation } from "angular-animations";
import { filter } from "rxjs";
import { CountryService } from "src/app/angular-core/services/country.service";
import { SaveSheetService } from "src/app/angular-core/services/save-sheet.service";
import { Address, BasicXpacInfo, DetailedXpacInfo } from "src/app/generated/CrossPoint.RemoteXpac.Server";
import { PermissionsService } from "src/app/services/permissions.service";
import { IRequiresPermissions, IPermission } from "src/app/shared/guards/authorized.guard";
import { getStoreInfoAction, setStoreInfoAction } from "src/app/store/actions/devices.actions";
import { selectStoreInfo } from "src/app/store/selectors/devices.selectors";
import { OperationEnum } from "src/app/utils/enums/operation.enums";
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { hasPermission } from "src/app/utils/functions/permission.function";
import { AllApiPermissions, IApiPermissions } from "src/app/utils/interfaces/auth.interfaces";
import { IRemoteXPACPortalState } from "src/app/utils/interfaces/store.interfaces";

@UntilDestroy()
@Component({
  selector: 'xpac-store-info ',
  templateUrl: './store-info.component.html',
  styleUrls: ['./store-info.component.scss'],
  animations: [
    fadeInOnEnterAnimation(),
    fadeOutOnLeaveAnimation(),
    fadeInExpandOnEnterAnimation(),
    fadeOutCollapseOnLeaveAnimation()
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreInfoComponent implements OnInit, IRequiresPermissions {

  private storeInfoLoaded: boolean = false;
  private permissions: IApiPermissions;


  public isLoading: boolean = true;
  @Input() public xpac: BasicXpacInfo = null;
  @Input() public readOnly: boolean = false;

  public formGroupStoreDetails: UntypedFormGroup;
  public reference: any;
  public storeInfo: DetailedXpacInfo;
  public countries: string[] = [];

  constructor(
    private saveSheetService: SaveSheetService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private countryService: CountryService,
  ) { }

  public ngOnInit(): void {
    this.initForm();
    this.initListeners();
    this.createReference(this.formGroupStoreDetails.value);
    this.onChanges();
  }

  public agGetRequiredPermissions(): IPermission[] {
    return [
      { type: 'Store', operation: OperationEnum.READ }
    ];
  }

  public hasUpdatePermission(permission: keyof AllApiPermissions): boolean {
    return !this.readOnly && hasPermission(permission, this.permissions, OperationEnum.UPDATE);
  }

  public hasStoreInfoUpdatePermission(): boolean {
    return !this.readOnly && hasPermission('Store', this.permissions, OperationEnum.UPDATE);
  }

  public onChanges(): void {
    this.formGroupStoreDetails.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.formGroupStoreDetails.dirty && this.formGroupStoreDetails.valid && !this.saveSheetService.openStatus$.value) {
          this.createSaveSheet();
        }
        else if (this.formGroupStoreDetails.dirty && this.saveSheetService.openStatus$.value) {
          if (this.checkFormPristine()) {
            this.formGroupStoreDetails.markAsPristine();
            this.saveSheetService.change(false);
          }
        }

        // Never allow saving of invalid forms
        if (!this.formGroupStoreDetails.valid) {
          this.saveSheetService.change(false);
        }
      })
  }

  public checkFormPristine() {
    for (var key in this.formGroupStoreDetails.value) {
      if (this.formGroupStoreDetails.value[key] != this.reference[key]) {
        if (this.formGroupStoreDetails.value[key] == "" && this.reference[key] == undefined) continue;
        return (false)
      };
    }
    return true;
  }

  public createSaveSheet() {
    this.saveSheetService.change(true);
  }

  public createReference(obj: any) {
    this.reference = Object.assign({}, obj)
  }

  public onAddressSearched(address: Address): void {
    this.formGroupStoreDetails.patchValue(address);
    this.formGroupStoreDetails.markAsDirty();
    this.formGroupStoreDetails.updateValueAndValidity();
  }

  private initForm(): void {
    this.formGroupStoreDetails = this.formBuilder.group({
      storeName: ['', [Validators.maxLength(50)]],
      name: ['', [Validators.maxLength(50)]],
      phone: ['', [Validators.minLength(6), Validators.maxLength(18)]],
      email: ['', [Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,64}$')]],
      street: ['', [Validators.maxLength(50)]],
      streetNumber: ['', [Validators.maxLength(50)]],
      zipCode: ['', [Validators.maxLength(50)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(50)]],
      country: ['', []],
      storeId: ['', [Validators.maxLength(50)]],
      retailerId: ['', [Validators.maxLength(50)]],
      groupId: ['', [Validators.maxLength(50)]],
      regionId: ['', [Validators.maxLength(50)]],
      customId: ['', [Validators.maxLength(50)]]
    });
  }

  private async initListeners(): Promise<void> {
    this.permissions = await this.permissionsService.getPermissions();

    // Update the countries when they are loaded
    this.countryService.getCountries()
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((countries: string[]) => {
        this.countries = countries;
      });

    // This component already requires permissions for this, so don't bother checking again
    this.store.dispatch(getStoreInfoAction({ data: this.xpac.deviceId }));

    this.store.select(selectStoreInfo(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((storeInfo: DetailedXpacInfo) => {
        this.storeInfo = storeInfo;
        this.setStoreInfoDataToStoreDetailsForm();
        this.storeInfoLoaded = true;
        this.isLoadingData();
      });

    this.saveSheetService.savedStatus$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((isSaved: boolean) => {
        if (isSaved) {
          const storeDetailsData = this.formGroupStoreDetails.getRawValue();

          if (this.hasStoreInfoUpdatePermission()) {
            this.store.dispatch(setStoreInfoAction({
              data: {
                deviceId: this.xpac.deviceId,
                patchStoreInfo: {
                  storeName: this.hasUpdatePermission('Store_StoreName') ? storeDetailsData.storeName : null,
                  supervisor: this.hasUpdatePermission('Store_Supervisor') ? {
                    email: storeDetailsData.email,
                    name: storeDetailsData.name,
                    phone: storeDetailsData.phone
                  } : null,
                  address: this.hasUpdatePermission('Store_Address') ? {
                    city: storeDetailsData.city,
                    country: storeDetailsData.country,
                    state: storeDetailsData.state,
                    street: storeDetailsData.street,
                    streetNumber: storeDetailsData.streetNumber,
                    zipCode: storeDetailsData.zipCode,
                  } : null,
                  location: null,
                  storeId: this.hasUpdatePermission('Store_StoreId') ? storeDetailsData.storeId : null,
                  retailerId: this.hasUpdatePermission('Store_RetailerId') ? storeDetailsData.retailerId : null,
                  groupId: this.hasUpdatePermission('Store_GroupId') ? storeDetailsData.groupId : null,
                  regionId: this.hasUpdatePermission('Store_RegionId') ? storeDetailsData.regionId : null,
                  customId: this.hasUpdatePermission('Store_CustomId') ? storeDetailsData.customId : null,
                }
              }
            }));
          }

          this.reference = Object.assign({}, this.formGroupStoreDetails.value);
        }
      });
  }

  private isLoadingData(): void {
    this.isLoading = !this.storeInfoLoaded;
    this.cdr.markForCheck();
  }

  private setStoreInfoDataToStoreDetailsForm(): void {
    const enabled: boolean = this.hasStoreInfoUpdatePermission();
    const hasStoreCodesPermission: boolean =
      this.hasUpdatePermission('Store_StoreId') &&
      this.hasUpdatePermission('Store_RetailerId') &&
      this.hasUpdatePermission('Store_GroupId') &&
      this.hasUpdatePermission('Store_RegionId') &&
      this.hasUpdatePermission('Store_CustomId');

    this.setStoreFormValues(this.storeInfo, enabled);
    this.setStoreFormValues(this.storeInfo.supervisor, this.hasUpdatePermission('Store_Supervisor'));
    this.setStoreFormValues(this.storeInfo.storeAddress, this.hasUpdatePermission('Store_Address'));
    this.setStoreFormValues(this.storeInfo.storeCodes, hasStoreCodesPermission);

    this.formGroupStoreDetails.markAsPristine();
  }

  private setStoreFormValues<T>(template: T, enabled: boolean): void {
    Object.keys(template).forEach((control: string) => {
      const abstractControl: AbstractControl = this.formGroupStoreDetails.get(control);
      if (abstractControl) {
        abstractControl.setValue(template[control as keyof T], { emitEvent: false });
        if (enabled) {
          abstractControl.enable();
        }
        else {
          abstractControl.disable();
        }
      }
    });
  }
}
